import React, { useEffect, useState } from "react";
import { exportTrialBalanceToExcel } from "./excelExportUtils";
import ExportExcelButton from "./ExportExcelButton";

function TrialBalanceOpening(props) {
    let apiPaths_from_local_storage = localStorage.getItem("apiPaths")
        ? JSON.parse(localStorage.getItem("apiPaths"))
        : "-";

    const { datagetTrialBalanceReport, parentDetails, selected_mode_table_structure } = props;
    const [expandedNodes, setExpandedNodes] = useState({});
    const [expandedGroups, setExpandedGroups] = useState({});

    useEffect(() => {
        if (datagetTrialBalanceReport) {
            const allNodes = {};
            const allGroups = {};

            datagetTrialBalanceReport.forEach((row) => {
                allNodes[row.NODE] = true;
                const groupKey = `${row.NODE}_${row["GROUP CODE"]}`;
                allGroups[groupKey] = true;
            });

            setExpandedNodes(allNodes);
            setExpandedGroups(allGroups);
        }
    }, [datagetTrialBalanceReport]);

    const handleExportExcel = () => {
        const selectedModeMeta = selected_mode_table_structure.find(
            (item) => item.mode_table_name === parentDetails.mode_list_array.find((m) => m.is_selected)?.mode_name
        );
        const visibleColumns = selectedModeMeta?.mode_table_columns.filter(
            (col) => col.column_key_from_api !== "NODE" && col.column_key_from_api !== "BALANCE"
        ) || [];

        exportTrialBalanceToExcel({
            data: datagetTrialBalanceReport,
            columns: visibleColumns,
            groupKeys: ["NODE", "GROUP CODE"],
            valueKeys: ["DEBIT", "CREDIT"],
            deriveFromBalance: false,
            appendBalanceColumn: true,
            fileName: selectedModeMeta?.mode_table_name || "TrialBalanceOpening",
            sheetName: "Trial Balance",
        });
    };

    // Shared calculations for visible columns, colSpans, and totals
    const selectedModeMeta = selected_mode_table_structure.find(
        (item) => item.mode_table_name === parentDetails.mode_list_array.find((m) => m.is_selected)?.mode_name
    );

    const visibleColumns = selectedModeMeta?.mode_table_columns.filter(
        (col) => col.column_key_from_api !== "NODE" && col.column_key_from_api !== "BALANCE"
    ) || [];

    const debitIndex = visibleColumns.findIndex((col) => col.column_key_from_api === "DEBIT");
    const labelColSpan = debitIndex > 0 ? debitIndex : 1;

    const calcTotal = (rowArr) => (rowArr || []).reduce(
        (acc, r) => ({
            DEBIT: acc.DEBIT + (r.DEBIT || 0),
            CREDIT: acc.CREDIT + (r.CREDIT || 0),
        }),
        { DEBIT: 0, CREDIT: 0 }
    );

    const grandTotal = calcTotal(datagetTrialBalanceReport || []);
    const reportBalance = grandTotal.DEBIT - grandTotal.CREDIT;

    return (
        <div className="min-h-screen bg-gray-200 py-6 px-4">
            <div className="max-w-4xl mx-auto bg-white shadow-xl rounded-none border border-gray-300 px-4 pt-4 pb-4 h-[85vh] flex flex-col">
                
                {/* FIXED TOP HEADER */}
                <div className="flex-none">
                    <div>
                        {apiPaths_from_local_storage[0]?.REP_HEADER?.includes("data:image/") && (
                            <img src={apiPaths_from_local_storage[0].REP_HEADER} alt="Report Header" />
                        )}
                    </div>

                    <div style={{
                        fontSize: '18px',
                        fontWeight: '500',
                        color: '#0f172a',
                        textAlign: 'center',
                        textTransform: 'uppercase',
                        margin: '12px 0',
                        padding: '8px 16px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '6px'
                    }}>
                        {selectedModeMeta?.mode_table_name}
                    </div>

                    {/* <ExportExcelButton onClick={handleExportExcel} /> */}
                </div>

                {/* SCROLLABLE TABLE BODY */}
                <div className="flex-1 overflow-y-auto border-b border-gray-200 mt-3">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="border-b-2 border-gray-900 sticky top-0 bg-white z-10">
                            <tr>
                                {visibleColumns.map((item, i) => (
                                    <th
                                        key={i}
                                        className={`px-4 py-2 text-xs font-bold text-gray-900 uppercase tracking-wider ${
                                            ["DEBIT", "CREDIT"].includes(item.column_key_from_api) ? "text-right" : "text-left"
                                        }`}
                                    >
                                        {item.column_name}
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        <tbody className="divide-y divide-gray-100">
                            {Object.entries(
                                (datagetTrialBalanceReport || []).reduce((acc, row) => {
                                    const nodeKey = row.NODE || "UNKNOWN NODE";
                                    const groupKey = row["GROUP CODE"] ?? row["GROUP_CODE"] ?? "UNKNOWN GROUP";

                                    if (!acc[nodeKey]) acc[nodeKey] = {};
                                    if (!acc[nodeKey][groupKey]) acc[nodeKey][groupKey] = [];
                                    acc[nodeKey][groupKey].push(row);
                                    return acc;
                                }, {})
                            ).map(([node, groups], nodeIndex) => {
                                const nodeSubtotal = calcTotal(Object.values(groups).flat());
                                const isNodeExpanded = expandedNodes[node] !== false;

                                return (
                                    <React.Fragment key={nodeIndex}>
                                        {/* NODE HEADER */}
                                        <tr
                                            className="border-t border-gray-200 cursor-pointer hover:bg-gray-50"
                                            onClick={() => setExpandedNodes((prev) => ({ ...prev, [node]: !isNodeExpanded }))}
                                        >
                                            <td colSpan={visibleColumns.length} className="px-4 py-2 font-bold text-gray-900 text-sm uppercase tracking-wide">
                                                <div className="flex items-center gap-2">
                                                    <span className={`transition-transform ${isNodeExpanded ? "rotate-90" : ""}`}>▶</span>
                                                    {node}
                                                </div>
                                            </td>
                                        </tr>

                                        {/* GROUPS UNDER NODE */}
                                        {isNodeExpanded &&
                                            Object.entries(groups).map(([groupCode, rows], groupIndex) => {
                                                const groupSubtotal = calcTotal(rows);
                                                const fullGroupKey = `${node}_${groupCode}`;
                                                const isGroupExpanded = expandedGroups[fullGroupKey] !== false;

                                                return (
                                                    <React.Fragment key={groupIndex}>
                                                        {/* GROUP HEADER */}
                                                        <tr
                                                            className="bg-gray-50 cursor-pointer hover:bg-gray-100"
                                                            onClick={() => setExpandedGroups((prev) => ({ ...prev, [fullGroupKey]: !isGroupExpanded }))}
                                                        >
                                                            <td colSpan={visibleColumns.length} className="px-4 py-2 font-semibold text-gray-700 text-xs uppercase tracking-wide pl-8">
                                                                <div className="flex items-center gap-2">
                                                                    <span className={`transition-transform ${isGroupExpanded ? "rotate-90" : ""}`}>▶</span>
                                                                    GROUP CODE: {groupCode}
                                                                </div>
                                                            </td>
                                                        </tr>

                                                        {/* ACCOUNTS UNDER GROUP */}
                                                        {isGroupExpanded &&
                                                            rows.map((row, i) => (
                                                                <tr key={i} className="hover:bg-gray-50">
                                                                    {visibleColumns.map((col, ci) => {
                                                                        const key = col.column_key_from_api;
                                                                        let displayValue = row[key];
                                                                        if (key === "DEBIT" || key === "CREDIT") {
                                                                            const val = row[key] || 0;
                                                                            displayValue = val ? val.toFixed(2) : "";
                                                                        }
                                                                        return (
                                                                            <td
                                                                                key={ci}
                                                                                className={`px-4 py-2 text-sm ${ci === 0 ? "pl-12" : ""} ${
                                                                                    ["DEBIT", "CREDIT"].includes(key)
                                                                                        ? "text-right tabular-nums font-mono"
                                                                                        : "text-left text-gray-700"
                                                                                } ${
                                                                                    key === "DEBIT"
                                                                                        ? "text-blue-700"
                                                                                        : key === "CREDIT"
                                                                                        ? "text-emerald-700"
                                                                                        : ""
                                                                                }`}
                                                                            >
                                                                                {displayValue}
                                                                            </td>
                                                                        );
                                                                    })}
                                                                </tr>
                                                            ))}

                                                        {/* GROUP SUBTOTAL */}
                                                        {isGroupExpanded && (
                                                            <tr className="border-t border-dashed border-gray-300 font-medium text-sm">
                                                                <td colSpan={labelColSpan} className="px-4 py-1.5 italic text-gray-500 text-xs pl-12">
                                                                    Subtotal — {groupCode}
                                                                </td>
                                                                <td className="px-4 py-1.5 text-right tabular-nums font-mono text-blue-700 text-xs">
                                                                    {groupSubtotal.DEBIT.toFixed(2)}
                                                                </td>
                                                                <td className="px-4 py-1.5 text-right tabular-nums font-mono text-emerald-700 text-xs">
                                                                    {groupSubtotal.CREDIT.toFixed(2)}
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}

                                        {/* NODE SUBTOTAL */}
                                        {isNodeExpanded && (
                                            <tr className="border-t border-dashed border-gray-300 font-medium text-sm">
                                                <td colSpan={labelColSpan} className="px-4 py-1.5 italic text-gray-500 text-xs">
                                                    Subtotal — {node}
                                                </td>
                                                <td className="px-4 py-1.5 text-right tabular-nums font-mono text-blue-700 text-xs">
                                                    {nodeSubtotal.DEBIT.toFixed(2)}
                                                </td>
                                                <td className="px-4 py-1.5 text-right tabular-nums font-mono text-emerald-700 text-xs">
                                                    {nodeSubtotal.CREDIT.toFixed(2)}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* FIXED BOTTOM FOOTER */}
                <div className="flex-none pt-2 bg-white">
                    <table className="min-w-full text-sm">
                        <tbody>
                            <tr className="font-bold text-sm" style={{ borderTop: "3px double #111827" }}>
                                <td colSpan={labelColSpan} className="px-4 py-2.5 text-gray-900 uppercase tracking-wide text-xs">
                                    Grand Total
                                </td>
                                <td className="px-4 py-2.5 text-right tabular-nums font-mono text-blue-800">
                                    {grandTotal.DEBIT.toFixed(2)}
                                </td>
                                <td className="px-4 py-2.5 text-right tabular-nums font-mono text-emerald-800">
                                    {grandTotal.CREDIT.toFixed(2)}
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div className="mt-2">
                        <div className="border-t border-gray-300" />
                        <div className="mt-3 flex justify-between items-center">
                            <p className="text-xs text-gray-400">
                                Generated: {new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                            </p>
                            <p className="text-xs font-semibold text-gray-700">
                                Balance: <span className="font-mono tabular-nums">{reportBalance.toFixed(2)}</span>
                            </p>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
}

export default TrialBalanceOpening;